import type { PackageSettings, PackageSettingsMatch, PackageSettingsMatchObject } from '@ms-cloudpack/common-types';
import { satisfies } from 'semver';
import { validateMatch } from './validateMatch.js';

interface CheckMatchParams {
  /** The name of the package. */
  name: string;
  /** The version of the package. */
  version: string;
  /** The match to check against. For an array, returns true if any entries match. */
  match: PackageSettings['match'];
  /** If true, require an exact match for the name (don't process wildcards). */
  exactMatch?: boolean;
}

/**
 * Checks if the package is a match for the given package settings.
 *
 * Throws if the match is invalid (shouldn't happen due to previous validation).
 */
export function checkMatch(params: CheckMatchParams): boolean {
  const { name, version, match, exactMatch } = params;

  if (Array.isArray(match)) {
    return checkMatchArray({ ...params, matches: match });
  }

  const matchObj = toMatchObject(match);
  const { name: matchName, version: matchVersion } = matchObj;

  if (matchName.startsWith('!')) {
    return checkNegatedMatch({ ...params, match: matchObj });
  }

  // For exact or non-wildcard matches, check the full name and possibly the version.
  if (exactMatch || !matchName.endsWith('*')) {
    return matchName === name && (!matchVersion || satisfies(version, matchVersion));
  }

  // Remove the trailing wildcard and check for a prefix match and possibly the version.
  return name.startsWith(matchName.slice(0, -1)) && (!matchVersion || satisfies(version, matchVersion));
}

/**
 * Check a match where `match.name` is negated. (This will handle removing the ! from the name.)
 *
 * Throws if the match has a version (shouldn't happen due to previous validation).
 */
function checkNegatedMatch(params: Omit<CheckMatchParams, 'match'> & { match: PackageSettingsMatchObject }): boolean {
  const result = validateMatch(params.match);
  if (!result.isValid) {
    throw new Error(result.error);
  }
  return !checkMatch({ ...params, match: { ...params.match, name: params.match.name.slice(1) } });
}

function checkMatchArray(params: Omit<CheckMatchParams, 'match'> & { matches: PackageSettingsMatch[] }): boolean {
  let hasMatch = false;
  // Now that we support negations, we have to check every element.
  for (const m of params.matches) {
    const matchObj = toMatchObject(m);
    if (matchObj.name.startsWith('!')) {
      hasMatch &&= checkNegatedMatch({ ...params, match: matchObj });
    } else {
      hasMatch ||= checkMatch({ ...params, match: m });
    }
  }
  return hasMatch;
}

export function toMatchObject(match: PackageSettingsMatchObject | string): PackageSettingsMatchObject {
  let name: string;
  let version: string | undefined;
  if (typeof match === 'string') {
    name = match;
  } else {
    name = match.name;
    // If any version is allowed, unset the version to skip the satisfies() check for efficiency.
    version = match.version === '*' ? undefined : match.version;
  }
  return { name, version };
}
